import { type CSSProperties, type PropsWithChildren, type ReactNode, createElement } from 'react';

import Success from '@/assets/svg/bx-check-circle.svg?react';
import Warning from '@/assets/svg/bx-error.svg?react';
import Error from '@/assets/svg/bx-x-circle.svg?react';
import { Text, Title } from '@/components/typography';
import { cn } from '@/lib/utils';

type StatusColorType = 'red' | 'green' | 'yellow';

const IconMap = {
  error: Error,
  success: Success,
  warning: Warning,
};

const colorMap: Record<string, StatusColorType> = {
  error: 'red',
  success: 'green',
  warning: 'yellow',
};

export type ResultsStatusType = keyof typeof IconMap;

type Props = {
  className?: string;
  style?: CSSProperties;
  status?: ResultsStatusType;
  icon?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  extra?: ReactNode;
  isFullPage?: boolean;
};

const statusColorClasses: Record<StatusColorType, string> = {
  red: cn('text-red-400'),
  green: cn('text-green-400'),
  yellow: cn('text-yellow-400'),
};

const getStatusIcon = (status?: ResultsStatusType) => {
  const statusIcon = status && IconMap[status] ? createElement(IconMap[status]) : undefined;

  if (status && statusIcon) {
    return <span className={statusColorClasses[colorMap[status]]}>{statusIcon}</span>;
  }
};

const Results = ({
  className,
  style,
  children,
  status,
  icon,
  title,
  subtitle,
  extra,
  isFullPage,
}: PropsWithChildren<Props>) => {
  const statusIcon = getStatusIcon(status);

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center px-8 py-6',
        isFullPage && 'mx-auto my-0 max-w-125 min-w-75 px-8 py-[10%]',
        className,
      )}
      style={style}
    >
      {(icon ?? statusIcon) && (
        <div className="mb-2 size-22.5 text-gray-800">{icon ?? statusIcon}</div>
      )}
      {title && (
        <Title className="my-2 text-center" level="2" weight="light">
          {title}
        </Title>
      )}
      {subtitle && (
        <Text className="text-center" variant="body1" color="grey">
          {subtitle}
        </Text>
      )}
      {extra && <div className="mt-6 text-center">{extra}</div>}
      {children && <div className="mt-6 self-stretch bg-gray-100 px-10.5 py-6">{children}</div>}
    </div>
  );
};

export default Results;
